import React,{useEffect,useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {feedbackService} from '../../services/feedbackService';
import {moduleLabel} from '../../data/moduleLabels';

const {FiCheck,FiRotateCcw,FiTrash2,FiPaperclip}=FiIcons;

const FILTERS=[
  {id: 'todos',label: 'Todos'},
  {id: 'nuevo',label: 'Nuevos'},
  {id: 'atendido',label: 'Atendidos'},
];

const formatDate=(iso)=> new Date(iso).toLocaleString('es-MX',{
  timeZone: 'America/Mexico_City',day: 'numeric',month: 'short',year: 'numeric',hour: '2-digit',minute: '2-digit',hourCycle: 'h23',
});

const formatSize=(bytes)=> bytes<1024*1024 ? `${Math.max(1,Math.round(bytes/1024))} KB` : `${(bytes/1024/1024).toFixed(1)} MB`;

export default function FeedbackManagement() {
  const [items,setItems]=useState(null);
  const [filter,setFilter]=useState('nuevo');
  const [error,setError]=useState('');

  useEffect(()=>{
    feedbackService.list().then(setItems).catch(err=> setError(err.message));
  },[]);

  const setStatus=async (item,status)=>{
    try {
      await feedbackService.setStatus(item.id,status);
      setItems(items.map(other=> other.id===item.id ? {...other,status} : other));
    } catch (err) {
      alert(err.message);
    }
  };

  const remove=async (item)=>{
    if (!window.confirm('¿Eliminar este comentario y sus archivos?')) return;
    try {
      await feedbackService.remove(item.id);
      setItems(items.filter(other=> other.id!==item.id));
    } catch (err) {
      alert(err.message);
    }
  };

  const visible=(items||[]).filter(item=> filter==='todos' || item.status===filter);
  const pending=(items||[]).filter(item=> item.status==='nuevo').length;

  return (
    <div className="max-w-5xl mx-auto px-6 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-10">
        <div>
          <span className="text-red-500 font-black tracking-[0.4em] uppercase text-[10px] mb-4 block">Administración</span>
          <h2 className="text-5xl font-black uppercase tracking-tighter text-white">Comentarios</h2>
          {items && <p className="text-zinc-300 text-sm mt-2">{pending} {pending===1 ? 'pendiente' : 'pendientes'} de {items.length}</p>}
        </div>
        <div className="flex bg-black/30 border border-white/10 rounded-2xl p-1" role="group" aria-label="Estado">
          {FILTERS.map(option=> (
            <button
              key={option.id}
              onClick={()=> setFilter(option.id)}
              aria-pressed={filter===option.id}
              className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${filter===option.id ? 'bg-red-600 text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm font-bold mb-6">{error}</p>}
      {!items && !error && <p className="text-zinc-400 text-[10px] font-black uppercase tracking-[0.3em]">Cargando…</p>}
      {items && visible.length===0 && <p className="text-zinc-300 text-sm">No hay comentarios en esta vista.</p>}

      <div className="space-y-4">
        {visible.map(item=> (
          <article key={item.id} className="glass-card bg-black/40 rounded-3xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h3 className="text-lg font-black text-white tracking-tight">{item.subject}</h3>
                  <span className={`px-3 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${item.status==='nuevo' ? 'bg-red-600 text-white' : 'bg-green-500/20 text-green-300'}`}>
                    {item.status==='nuevo' ? 'Nuevo' : 'Atendido'}
                  </span>
                </div>
                <p className="text-zinc-300 text-xs">
                  <span className="font-bold text-white">{item.userName}</span> · {item.userEmail} · {formatDate(item.createdAt)}
                  {item.page && <> · desde {moduleLabel(item.page)}</>}
                </p>
              </div>

              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={()=> setStatus(item,item.status==='nuevo' ? 'atendido' : 'nuevo')}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-[10px] font-black uppercase tracking-widest text-white flex items-center gap-2 transition-all"
                >
                  <SafeIcon icon={item.status==='nuevo' ? FiCheck : FiRotateCcw} />
                  {item.status==='nuevo' ? 'Marcar atendido' : 'Reabrir'}
                </button>
                <button onClick={()=> remove(item)} aria-label="Eliminar" className="p-3 bg-white/10 hover:bg-red-600 rounded-xl text-white transition-all">
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>

            <p className="text-white text-sm leading-relaxed whitespace-pre-wrap break-words mt-5">{item.message}</p>

            {item.attachments.length>0 && (
              <div className="flex flex-wrap gap-3 mt-5">
                {item.attachments.map(file=> {
                  const url=feedbackService.attachmentUrl(item.id,file.id);
                  return (
                    <a
                      key={file.id}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      title={file.name}
                      className="group flex items-center gap-3 p-2 pr-4 bg-white/10 hover:bg-white/20 border border-white/10 rounded-2xl transition-all"
                    >
                      {file.type.startsWith('image/')
                        ? <img src={url} alt={file.name} className="w-14 h-14 object-cover rounded-xl bg-white" />
                        : <span className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center"><SafeIcon icon={FiPaperclip} className="w-5 h-5" /></span>}
                      <span className="text-xs">
                        <span className="block font-bold text-white max-w-[10rem] truncate">{file.name}</span>
                        <span className="text-zinc-400">{formatSize(file.size)}</span>
                      </span>
                    </a>
                  );
                })}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
